import React, { useState, useEffect, createContext, useContext } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import Login from './pages/Login'
import Dashboard from './pages/Dashboard'
import AllocationDetail from './pages/AllocationDetail'
import AddPassenger from './pages/AddPassenger'
import Passengers from './pages/Passengers'
import Reports from './pages/Reports'
import Profile from './pages/Profile'

// Auth Context
interface User {
  id: number
  name: string
  email: string
  agencyId: number
  agencyName: string
}

interface AuthContextType {
  user: User | null
  token: string | null
  login: (email: string, password: string, whatsappNumber: string) => Promise<void>
  logout: () => void
  isLoading: boolean
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  token: null,
  login: async () => {},
  logout: () => {},
  isLoading: true,
})

export const useAuth = () => useContext(AuthContext)

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500"></div>
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

function App() {
  const [user, setUser] = useState<User | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // Check for existing session on mount
  useEffect(() => {
    const storedToken = localStorage.getItem('agent_token')
    const storedUser = localStorage.getItem('agent_user')

    if (storedToken && storedUser) {
      try {
        setToken(storedToken)
        setUser(JSON.parse(storedUser))
      } catch (e) {
        localStorage.removeItem('agent_token')
        localStorage.removeItem('agent_user')
      }
    }
    setIsLoading(false)
  }, [])

  const login = async (email: string, password: string, whatsappNumber: string) => {
    // Call the main API for authentication
    const API_BASE = import.meta.env.VITE_API_BASE_URL || 'https://jetnova-jul23-dashboard.zakonweb.workers.dev'
    
    const response = await fetch(`${API_BASE}/api/v1/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, whatsappNumber })
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error?.error || error?.message || `Login failed (${response.status})`)
    }

    const data = await response.json()
    
    // Store credentials
    setToken(data.token)
    setUser({
      id: data.user.id,
      name: data.user.name,
      email: data.user.email,
      agencyId: data.user.linkedAgencyId || data.user.agency_id,
      agencyName: data.user.agencyName || 'Unknown Agency'
    })
    
    localStorage.setItem('agent_token', data.token)
    localStorage.setItem('agent_user', JSON.stringify({
      id: data.user.id,
      name: data.user.name,
      email: data.user.email,
      agencyId: data.user.linkedAgencyId || data.user.agency_id,
      agencyName: data.user.agencyName || 'Unknown Agency'
    }))
  }

  const logout = () => {
    setUser(null)
    setToken(null)
    localStorage.removeItem('agent_token')
    localStorage.removeItem('agent_user')
  }

  return (
    <AuthContext.Provider value={{ user, token, login, logout, isLoading }}>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/allocation/:id"
            element={
              <ProtectedRoute>
                <AllocationDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/allocation/:id/add-passenger"
            element={
              <ProtectedRoute>
                <AddPassenger />
              </ProtectedRoute>
            }
          />
          <Route
            path="/passengers"
            element={
              <ProtectedRoute>
                <Passengers />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports"
            element={
              <ProtectedRoute>
                <Reports />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthContext.Provider>
  )
}

export default App
